/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    button : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center',
      minHeight      : 40,
      padding        : '0 15px',
      height         : '100%',
      width          : ({fullWidth}: {fullWidth: boolean}) => fullWidth ? '100%' : 'auto',
      '&:hover'      : {
        backgroundColor : dlsColor('grey6')
      }
    }
  };
});

export type ViewMoreProps = {
  fontSize?: string,
  loading?: boolean,
  fullWidth?: boolean,
  handleLoadMore: () => void
}

/**
 * @ViewMoreButton
 *
 * ViewMoreButton component is a simple button that is used to load more items via
 * a callback function.
 *
 * ```typescript
 *
 * import {ViewMoreButton} from '@ion/dls/src/components';
 *
 * <ViewMoreButton handleLoadMore={handleLoadMore} loading={isFetching} fontSize={'14px'} />
 *
 * ```
 *
 * @param handleLoadMore - function to handle load more button click
 * @param loading - whether the button is loading or not
 * @param fontSize - font size of the button
 * @param fullWidth - whether the button should be full width or not
 *
 */
const ViewMoreButton = ({
  handleLoadMore,
  loading,
  fontSize = '12px',
  fullWidth = true
}: ViewMoreProps) => {
  const classes = useStyles({fullWidth});

  return (
    <TertiaryButton className={classes.button}
      onClick={handleLoadMore}
      fontSize={fontSize}
      noUnderline>
      {loading ? (
        <ButtonSpinner size={14} />
      ) : (
        translate('common:actions.viewMore')
      )}
    </TertiaryButton>
  );
};

export default ViewMoreButton;
